import React from 'react';
import { Box, Typography, Button, Container, Grid, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WavesIcon from '@mui/icons-material/Waves';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import { useStateValue } from '../../../contexto/store';

const LandingHero = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [{ sesionUsuario }] = useStateValue();
  const isAuthenticated = sesionUsuario ? sesionUsuario.autenticado : false;

  return (
    <Box
      id="hero"
      sx={{
        minHeight: '90vh',
        background: 'linear-gradient(135deg, #00241b 0%, #004D40 100%)',
        color: 'white',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        pt: { xs: 8, md: 0 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(25, 118, 210, 0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(0, 230, 118, 0.2) 0%, transparent 40%)',
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4} sx={{ alignItems: "center" }}>
          <Grid >
            <Box sx={{ animation: 'fadeInLeft 1s ease-out' }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  lineHeight: 1.1,
                  mb: 2,
                  background: 'linear-gradient(to right, #fff 30%, #4DB6AC 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                GESTIÓN INTELIGENTE DEL AGUA
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  fontWeight: 400,
                  color: 'rgba(255,255,255,0.8)',
                  maxWidth: '600px',
                  lineHeight: 1.6
                }}
              >
                Impulsando la sostenibilidad y eficiencia en el suministro de agua potable con tecnología de vanguardia para nuestra comunidad.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/auth/login')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: '50px',
                    bgcolor: '#00e676',
                    color: '#002626',
                    '&:hover': { bgcolor: '#00c853' },
                    boxShadow: '0 10px 20px rgba(0, 230, 118, 0.3)'
                  }}
                >
                  ACCESO AL PANEL
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    document.getElementById('about').scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: '50px',
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  DESCUBRE MÁS
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid sx={{ display: { xs: 'none', md: 'block' } }}>
             <Box
               sx={{
                 position: 'relative',
                 width: '100%',
                 height: '400px',
                 animation: 'float 6s ease-in-out infinite',
                 display: 'flex',
                 justifyContent: 'center',
                 alignItems: 'center'
               }}
             >
                <Box
                  sx={{
                    position: 'absolute',
                    width: '300px',
                    height: '300px',
                    borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                    background: 'linear-gradient(45deg, #2196f3 0%, #00e676 100%)',
                    opacity: 0.6,
                    filter: 'blur(40px)',
                  }}
                />
                <WavesIcon sx={{ fontSize: '15rem', color: 'rgba(255,255,255,0.9)', zIndex: 1 }} />
             </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: 8, display: 'flex', gap: { xs: 2, md: 6 }, flexWrap: 'wrap', opacity: 0.7 }}>
           <StatItem icon={<SecurityIcon />} text="Seguridad 100%" />
           <StatItem icon={<SpeedIcon />} text="Alta Eficiencia" />
           <StatItem icon={<WavesIcon />} text="Recurso Sostenible" />
        </Box>
      </Container>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes float {
          0% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }
        @keyframes fadeInLeft {
          from { opacity: 0; transform: translateX(-50px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}} />
    </Box>
  );
};

const StatItem = ({ icon, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {React.cloneElement(icon, { sx: { fontSize: 24, color: '#4DB6AC' } })}
    <Typography variant="body1" sx={{ fontWeight: 500, letterSpacing: 1 }}>
      {text}
    </Typography>
  </Box>
);

export default LandingHero;
